// Admin > Radios > Edit > DMR settings (IRC380): "Read codeplug from radio". The radio reads the
// codeplug currently loaded in this app's DMR worker; a platform administrator may then save it as
// a NEW profile of the radio's network. Nothing here writes to a radio or loads a profile into one,
// and the codeplug itself never reaches the browser (the server stages it).
import { useEffect, useRef, useState } from 'react';
import {
  CodeplugApiError,
  discardCodeplugCapture,
  getCodeplugCapture,
  saveCodeplugCapture,
  startCodeplugCapture,
  type CodeplugCapture as Capture,
} from '../../services/dmrCodeplug';
import { getAuthConfig, getMe, signInWithMicrosoft } from '../../services/identity';
import {
  CAPTURE_POLL_MS,
  CAPTURE_TIMEOUT_MS,
  CAPTURE_TIMEOUT_TEXT,
  NOT_LOADED_STATEMENT,
  captureAccess,
  captureAccessText,
  canSaveCapture,
  captureButtonState,
  captureConfirmText,
  captureErrorExplanation,
  captureNameProblem,
  captureStatusText,
  networkLabel,
  savedText,
  warningText,
  type CaptureAccess,
  type CapturePhase,
} from './codeplugCaptureModel';

const btn = 'px-3 py-1.5 rounded text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed';
const input = 'w-full px-2 py-1.5 bg-gray-900 border border-gray-600 rounded text-white text-sm';

interface Props {
  radioId: string;
  orgId: string;
  /** The rest of the DMR settings section is busy / its codeplug editor is open. */
  sectionBusy: boolean;
  draftOpen: boolean;
  /** Called after a profile was saved, so the section can refresh its profile list. */
  onSaved?: () => void;
}

/**
 * Looks up the caller once. Only platform administrators with a verified sign-in get the button
 * (never a DEV_MODE identity); this is UX only - the server is the security boundary and refuses
 * everyone else. A DEV_MODE identity is pointed to Microsoft sign-in where that can work.
 */
export default function CodeplugCapture(props: Props) {
  const [me, setMe] = useState<{ access: CaptureAccess; networkName?: string } | null>(null);
  useEffect(() => {
    let alive = true;
    Promise.all([getMe(), getAuthConfig()])
      .then(([m, cfg]) =>
        alive && setMe({ access: captureAccess(m, cfg.entra_enabled, window.location.origin), networkName: m?.networks.find((n) => n.id === props.orgId)?.name })
      )
      .catch(() => alive && setMe({ access: 'unknown' }));
    return () => {
      alive = false;
    };
  }, []);
  if (!me) return null;
  return <CodeplugCaptureView {...props} platformAdmin={me.access === 'allowed'} access={me.access} networkName={me.networkName} />;
}

/** Shown instead of the button: why it is not offered, and a sign-in where that can help. */
export function CaptureAccessHint({ access }: { access: CaptureAccess }) {
  return (
    <div className="space-y-2" data-capture-admin-only data-capture-access={access}>
      <p className="text-xs text-gray-400">{captureAccessText(access)}</p>
      {access === 'dev-sign-in' && (
        <button type="button" className={`${btn} bg-white hover:bg-gray-100 text-gray-900`} onClick={signInWithMicrosoft} data-capture-sign-in>
          Sign in with Microsoft
        </button>
      )}
    </div>
  );
}

/** The counts, warnings and exclusions of a READY reading, and what saving it does not do. */
export function CaptureDetails({ capture }: { capture: Capture }) {
  const s = capture.summary;
  return (
    <div className="space-y-2 text-sm" data-capture-details>
      {s && (
        <p className="text-gray-200" data-capture-summary>
          Read: {s.contacts} contacts, {s.rx_group_lists} RX group lists, {s.channels} channels, {s.zones} zones.
        </p>
      )}
      {capture.warnings.length > 0 && (
        <ul className="list-disc pl-5 text-amber-300" data-capture-warnings>
          {capture.warnings.map((w, i) => <li key={`${w.code}-${i}`}>{warningText(w)}</li>)}
        </ul>
      )}
      {capture.excluded.length > 0 && (
        <div className="text-gray-300" data-capture-excluded>
          <p>Never part of a profile (not read into it):</p>
          <ul className="list-disc pl-5 text-gray-400">
            {capture.excluded.map((x) => <li key={x}>{x}</li>)}
          </ul>
        </div>
      )}
      <p className="text-amber-200 font-semibold" data-capture-not-loaded>{NOT_LOADED_STATEMENT}</p>
    </div>
  );
}

interface FormProps {
  capture: Capture;
  name: string;
  description: string;
  confirmed: boolean;
  saving: boolean;
  onName: (v: string) => void;
  onDescription: (v: string) => void;
  onConfirmed: (v: boolean) => void;
  onSave: () => void;
  onDiscard: () => void;
}

/** Saving a READY reading as a new profile: name, description and the network confirmation. */
export function CaptureSaveForm(p: FormProps) {
  const nameProblem = captureNameProblem(p.name);
  return (
    <div className="space-y-2 border border-amber-800 rounded p-3" data-capture-save-form>
      <CaptureDetails capture={p.capture} />
      <label className="block text-sm text-gray-300">
        New profile name
        <input className={input} value={p.name} maxLength={200} disabled={p.saving} onChange={(e) => p.onName(e.target.value)} aria-label="New profile name" />
      </label>
      {nameProblem && <p className="text-xs text-red-300">{nameProblem}</p>}
      <label className="block text-sm text-gray-300">
        Description (optional)
        <textarea className={input} rows={2} value={p.description} disabled={p.saving} onChange={(e) => p.onDescription(e.target.value)} aria-label="Description" />
      </label>
      <label className="flex items-start gap-2 text-sm text-gray-200">
        <input type="checkbox" className="mt-1" checked={p.confirmed} disabled={p.saving} onChange={(e) => p.onConfirmed(e.target.checked)} data-capture-confirm />
        <span>I confirm the codeplug currently on this radio belongs to network "{p.capture.network_name}"</span>
      </label>
      <div className="flex gap-2">
        <button type="button" className={`${btn} bg-blue-600 hover:bg-blue-500 text-white`} disabled={!canSaveCapture(p.name, p.confirmed, p.saving)} onClick={p.onSave} data-capture-save>
          Save as new profile
        </button>
        <button type="button" className={`${btn} bg-gray-700 hover:bg-gray-600 text-white`} disabled={p.saving} onClick={p.onDiscard}>Discard</button>
      </div>
    </div>
  );
}

const describe = (e: unknown): string => {
  if (!(e instanceof Error)) return 'Request failed';
  const extra = e instanceof CodeplugApiError ? captureErrorExplanation(e.code) : '';
  return extra ? `${e.message} - ${extra}` : e.message;
};

export function CodeplugCaptureView({
  radioId,
  orgId,
  sectionBusy,
  draftOpen,
  onSaved,
  platformAdmin,
  access,
  networkName,
}: Props & { platformAdmin: boolean; access?: CaptureAccess; networkName?: string }) {
  const [phase, setPhase] = useState<CapturePhase>('idle');
  const [capture, setCapture] = useState<Capture | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  // Only the most recent reading may set state; unmounting (another radio/network) ends them all.
  const latest = useRef(0);
  useEffect(() => {
    return () => {
      latest.current++;
    };
  }, []);

  const state = captureButtonState({ platformAdmin, phase, sectionBusy, draftOpen });
  if (state === 'hidden') {
    return <CaptureAccessHint access={access && access !== 'allowed' ? access : 'unknown'} />;
  }

  const show = (c: Capture, seq: number) => {
    if (seq !== latest.current) return;
    setCapture(c);
    if (c.status === 'READY') {
      setPhase('ready');
      setName(c.default_name);
      setDescription('');
      setConfirmed(false);
    } else if (c.status === 'FAILED') {
      setPhase('failed');
      setError(`${captureStatusText(c)}${captureErrorExplanation(c.error_code) ? ` - ${captureErrorExplanation(c.error_code)}` : ''}`);
    } else if (c.status === 'SAVED') {
      setPhase('saved');
      setMessage(captureStatusText(c));
    }
  };

  const poll = async (seq: number, captureId: string, started: number) => {
    for (;;) {
      await new Promise((r) => setTimeout(r, CAPTURE_POLL_MS));
      if (seq !== latest.current) return;
      if (Date.now() - started > CAPTURE_TIMEOUT_MS) {
        setPhase('failed');
        setError(CAPTURE_TIMEOUT_TEXT);
        return;
      }
      let c: Capture;
      try {
        c = await getCodeplugCapture(radioId, captureId);
      } catch (e) {
        if (seq !== latest.current) return;
        setPhase('failed');
        // A pending reading the server dropped after its 45 s has expired: say it timed out.
        setError(e instanceof CodeplugApiError && e.code === 'CAPTURE_NOT_FOUND' ? CAPTURE_TIMEOUT_TEXT : describe(e));
        return;
      }
      if (seq !== latest.current) return;
      if (c.status !== 'PENDING') return show(c, seq);
    }
  };

  const start = async () => {
    if (!confirm(captureConfirmText(networkLabel(networkName, orgId)))) return;
    const seq = ++latest.current;
    setPhase('starting');
    setCapture(null);
    setError(null);
    setMessage(null);
    try {
      const c = await startCodeplugCapture(radioId);
      if (seq !== latest.current) return;
      if (c.status !== 'PENDING') return show(c, seq);
      setCapture(c);
      setPhase('pending');
      await poll(seq, c.capture_id, Date.now());
    } catch (e) {
      if (seq !== latest.current) return;
      setPhase('idle');
      setError(describe(e));
    }
  };

  const save = async () => {
    if (!capture || !canSaveCapture(name, confirmed, false)) return;
    const seq = latest.current;
    setPhase('saving');
    setError(null);
    try {
      const saved = await saveCodeplugCapture(radioId, capture.capture_id, name.trim(), description.trim());
      if (seq !== latest.current) return;
      setCapture(saved.capture);
      setPhase('saved');
      setMessage(savedText(saved.profile.name, saved.capture.network_name));
      onSaved?.();
    } catch (e) {
      if (seq !== latest.current) return;
      const gone = e instanceof CodeplugApiError && (e.code === 'CAPTURE_NOT_FOUND' || e.code === 'CAPTURE_STALE');
      // A taken name or a refused name keeps the form so the administrator can change it and retry.
      setPhase(gone ? 'idle' : 'ready');
      if (gone) setCapture(null);
      setError(describe(e));
    }
  };

  const discard = async () => {
    if (!capture) return;
    const seq = ++latest.current;
    setError(null);
    try {
      await discardCodeplugCapture(radioId, capture.capture_id);
      if (seq !== latest.current) return;
      setCapture(null);
      setPhase('idle');
      setMessage('The reading was discarded. Nothing was saved.');
    } catch (e) {
      if (seq !== latest.current) return;
      setError(describe(e));
    }
  };

  const showForm = capture && (phase === 'ready' || phase === 'saving');
  return (
    <div className="space-y-2 border-t border-gray-700 pt-3" data-codeplug-capture>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={`${btn} bg-indigo-700 hover:bg-indigo-600 text-white`} disabled={state === 'disabled'} onClick={start} data-capture-start>
          Læs codeplug fra radio
        </button>
        <span className="text-xs text-gray-500">Reads the codeplug loaded in this app's DMR worker to save it as a new profile. Writes nothing to the radio.</span>
      </div>
      {(phase === 'starting' || phase === 'pending') && (
        <p className="text-sm text-gray-300" data-capture-pending>Waiting for the radio to read the codeplug loaded in its DMR worker…</p>
      )}
      {error && <p className="text-sm text-red-300" role="alert">{error}</p>}
      {message && <p className="text-sm text-green-300" data-capture-message>{message}</p>}
      {showForm && (
        <CaptureSaveForm
          capture={capture}
          name={name}
          description={description}
          confirmed={confirmed}
          saving={phase === 'saving'}
          onName={setName}
          onDescription={setDescription}
          onConfirmed={setConfirmed}
          onSave={save}
          onDiscard={discard}
        />
      )}
    </div>
  );
}
